
import { defineComponent } from 'vue';
import SvgIcon from './SvgIcon.vue';

interface InternalState {
  icon: string;
  title: string;
  text?: string;
}

enum TIME_PERIODS {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month'
}

enum STATES {
  LOADING = 'loading',
  ERROR = 'error',
  EMPTY = 'empty',
  RESOLVED = ''
}

export default defineComponent({
  name: 'BaseStateIndicator',

  components: {
    SvgIcon
  },

  props: {
    state: {
      type: String,
      default: STATES.RESOLVED,
      validator(value: string) {
        return [...(Object.values(STATES) as string[])].includes(value);
      }
    },
    size: {
      type: String,
      default: 'md'
    },
    alignment: {
      type: String,
      default: 'center'
    },
    emptyText: {
      type: String,
      default: ''
    },
    emptyTitle: {
      type: String,
      default: ''
    },
    errorText: {
      type: String,
      default: ''
    },
    errorTitle: {
      type: String,
      default: ''
    },
    loadingText: {
      type: String,
      default: ''
    },
    loadingTitle: {
      type: String,
      default: ''
    },
    timePeriod: {
      type: String,
      default: undefined,
      validator(value: string) {
        return [...(Object.values(TIME_PERIODS) as string[])].includes(value);
      }
    },
    data: {
      type: [Array, Object],
      default: undefined
    }
  },

  computed: {
    internalEmptyText(): string {
      if (this.emptyText) {
        return this.emptyText;
      }
      if (this.timePeriod) {
        return this.$t('utils:BaseStateIndicator.empty.withTimePeriod', {
          period: this.$t(
            `utils:BaseStateIndicator.empty.period.${this.timePeriod}`
          )
        });
      }
      return '';
    },

    statesMap(): Record<string, InternalState> {
      return {
        loading: {
          icon: 'spinner',
          title: this.loadingTitle
            ? this.loadingTitle
            : this.$t('utils:BaseStateIndicator.loadingEllipses'),
          text: this.loadingText
        },
        error: {
          icon: 'error',
          title: this.errorTitle
            ? this.errorTitle
            : this.$t('utils:BaseStateIndicator.error'),
          text: this.errorText
        },
        empty: {
          icon: 'empty',
          title: this.emptyTitle
            ? this.emptyTitle
            : this.$t('utils:BaseStateIndicator.empty.base'),
          text: this.internalEmptyText
        }
      };
    },

    internalState(): string {
      const wasStateGiven = !!this.state;
      const wasDataGiven = !!this.data;

      const usePassedState = wasStateGiven || !wasDataGiven;

      if (usePassedState) {
        return this.state;
      }

      const isEmpty = Array.isArray(this.data)
        ? !this.data?.length
        : !Object.keys(this.data).length;

      return isEmpty ? STATES.EMPTY : this.state;
    },

    currentState(): InternalState {
      return this.statesMap[this.internalState] || ({} as InternalState);
    },
    hasActions(): boolean {
      return !!this.$slots.actions;
    },
    hasText(): boolean {
      return !!this.$slots.default;
    }
  },
  methods: {
    spaceUtilityClassMap(feature: string, size: string): string {
      const featureSpaceUtilityClassMap: Record<
        string,
        Record<string, string>
      > = {
        title: {
          sm: 'u-font-md u-margin-0x',
          md: 'u-font-lg u-margin-0x',
          lg: 'u-font-2xl u-margin-bottom-1x'
        },
        text: {
          sm: 'u-font-sm',
          md: 'u-font-md',
          lg: 'u-font-lg'
        },
        icon: {
          sm: 'u-margin-bottom-3x',
          md: 'u-margin-bottom-4x',
          lg: 'u-margin-bottom-8x'
        },
        actions: {
          sm: 'u-margin-top-2x',
          md: 'u-margin-top-4x',
          lg: 'u-margin-top-4x'
        }
      };
      return featureSpaceUtilityClassMap[feature][size];
    },
    sizeUtilityClassMap(feature: string, size: string): string {
      const featureSizeUtilityClassMap: Record<
        string,
        Record<string, string>
      > = {
        icon: {
          sm: 'lg',
          md: 'xl',
          lg: '3xl'
        }
      };
      return featureSizeUtilityClassMap[feature][size];
    }
  }
});
